import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  Typography, 
  Chip, 
  LinearProgress, 
  Box,
  Stack,
  Grid,
  IconButton
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { Apartment, Trip } from '@/types';
import ApartmentBill from '@/components/apartment/ApartmentBill';
import EditApartmentForm from '@/components/apartment/EditApartmentForm';
import { useI18n } from '@/hooks/useI18n';

interface ApartmentCardProps {
  apartment: Apartment;
  trip: Trip;
}

const ApartmentCard: React.FC<ApartmentCardProps> = ({ apartment, trip }) => {
  const { t } = useI18n();

  const getClassBadgeColor = (apartmentClass: string): 'success' | 'primary' | 'secondary' | 'default' => {
    switch (apartmentClass) {
      case 'economy':
        return 'success';
      case 'standard':
        return 'primary';
      case 'premium':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getClassEmoji = (apartmentClass: string) => {
    switch (apartmentClass) {
      case 'economy':
        return '💚';
      case 'standard':
        return '💙';
      case 'premium':
        return '💜';
      default:
        return '🏠';
    }
  };

  const getOccupancyPercentage = (current: number, max: number) => {
    return (current / max) * 100;
  };

  const getApartmentPrice = (apartment: Apartment) => {
    const type = trip.apartmentTypes.find(t => t.id === apartment.apartmentTypeId);
    if (!type) return 0;

    const occupancy = apartment.currentOccupancy;
    const priceRule = type.priceRules.find(rule => rule.occupancy === occupancy) ||
                     type.priceRules.find(rule => rule.occupancy <= occupancy) ||
                     type.priceRules[0];
    
    return priceRule ? priceRule.pricePerPersonPerWeek : 0;
  };

  const occupancyPercentage = getOccupancyPercentage(apartment.currentOccupancy, apartment.maxOccupancy);
  const pricePerPersonPerWeek = getApartmentPrice(apartment);
  const apartmentType = trip.apartmentTypes.find(t => t.id === apartment.apartmentTypeId);

  return (
    <Card sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      transition: 'box-shadow 0.3s ease-in-out',
      '&:hover': {
        boxShadow: (theme) => theme.shadows[8]
      }
    }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            🏠 {apartment.name}
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${getClassEmoji(apartment.class)} ${apartment.class.charAt(0).toUpperCase() + apartment.class.slice(1)}`}
              color={getClassBadgeColor(apartment.class)}
              size="small"
            />
            <EditApartmentForm
              tripId={trip.id}
              apartment={apartment}
              apartmentTypes={trip.apartmentTypes}
              trigger={
                <IconButton size="small" sx={{ p: 1 }}>
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              }
            />
          </Box>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              👥 {t('apartment.occupancy')}
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {apartment.currentOccupancy} / {apartment.maxOccupancy}
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate"
            value={occupancyPercentage} 
            sx={{ height: 8, borderRadius: 4 }}
          />

          <Grid container spacing={2}>
            <Grid size={6}>
            <Box>
              <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                💰 {t('apartment.pricePerPersonPerWeek')}
              </Typography>
              <Typography variant="body2" color="text.secondary">€ {pricePerPersonPerWeek}</Typography>
            </Box>
            </Grid>
            <Grid size={6}>
            <Box>
              <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                🛏️ {t('apartment.totalBeds')}
              </Typography>
              <Typography variant="body2" color="text.secondary">{apartment.maxOccupancy}</Typography>
            </Box>
            </Grid>
          </Grid>

          <Box>
            <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              🏷️ {t('apartment.apartmentType')}
            </Typography>
            <Typography variant="body2" color="text.secondary">{apartmentType ? apartmentType.name : t('apartment.notAssigned')}</Typography>
          </Box>

          {apartment.amenities.length > 0 && (
            <Box>
              <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                ✨ {t('apartment.amenities')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {apartment.amenities.map((amenity, index) => (
                  <Chip 
                    key={index} 
                    label={amenity}
                    variant="outlined" 
                    size="small"
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <ApartmentBill 
              apartment={apartment}
              trip={trip}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ApartmentCard;
